import { DuplicateSettings, RiskCategory } from "services/risk/types"

/** Shared constants for all risk/fraud management components */

// ─── Risk Level ─────────────────────────────────────────────────────────────

export const RISK_LEVEL_CONFIG: Record<number, { label: string; color: string; badgeClass: string }> = {
  1: { label: "Low Risk", color: "#22c55e", badgeClass: "badge-success" },
  2: { label: "Medium Risk", color: "#eab308", badgeClass: "badge-warning" },
  3: { label: "High Risk", color: "#f97316", badgeClass: "badge-accent" },
  4: { label: "Critical", color: "#ef4444", badgeClass: "badge-error" }
}

export const LEVEL_LABELS: Record<string, { label: string; cls: string }> = {
  LEVEL_1: { label: "Level 1 (Low)", cls: "badge-success" },
  LEVEL_2: { label: "Level 2 (Medium)", cls: "badge-warning" },
  LEVEL_3: { label: "Level 3 (High)", cls: "badge-error badge-outline" },
  LEVEL_4: { label: "Level 4 (Critical)", cls: "badge-error" }
}

// ─── Action badges ──────────────────────────────────────────────────────────

export const ACTION_BADGE_CONFIG: Record<string, { label: string; cls: string }> = {
  BAN_PLAYER: { label: "Ban", cls: "badge-error" },
  FLAG_FOR_REVIEW: { label: "Flag", cls: "badge-warning" },
  FORBID_BONUSES: { label: "No Bonus", cls: "badge-secondary" },
  DISABLE_WITHDRAWALS: { label: "Hold", cls: "badge-accent" },
  LIMIT_DEPOSITS: { label: "Limit Dep.", cls: "badge-accent" },
  REQUIRE_KYC: { label: "KYC", cls: "badge-info" },
  SEND_ALERT: { label: "Alert", cls: "badge-primary" }
}

// ─── Severity / Priority / Status badges ────────────────────────────────────

export const SEVERITY_BADGE: Record<string, string> = {
  NO_RISK: "badge-success",
  MEDIUM: "badge-warning",
  HIGH: "badge-error badge-outline",
  TOO_HIGH: "badge-error"
}

export const PRIORITY_BADGE: Record<string, string> = {
  HIGH: "badge-error",
  MEDIUM: "badge-warning",
  LOW: "badge-info"
}

export const STATUS_BADGE: Record<string, string> = {
  PENDING: "badge-warning",
  REVIEWED: "badge-success",
  DISMISSED: "badge-ghost"
}

// ─── Severity labels (used in settings) ─────────────────────────────────────

export type SeverityThresholdKey = keyof DuplicateSettings["thresholds"]

export const SEVERITY_THRESHOLD_KEYS: SeverityThresholdKey[] = ["medium", "high", "too_high"]

export const SEVERITY_LABELS: Record<SeverityThresholdKey, { label: string; cls: string }> = {
  medium: { label: "Medium", cls: "badge-warning" },
  high: { label: "High", cls: "badge-error badge-outline" },
  too_high: { label: "Too High", cls: "badge-error" }
}

// ─── Criteria labels (duplicate detection) ──────────────────────────────────

export const CRITERIA_LABELS: Record<string, string> = {
  payment_details: "Payment Details",
  device_info: "Device Info",
  browser_info: "Browser Info",
  ipv6: "IPv6 Address",
  email: "Email",
  full_name: "Full Name",
  address: "Address",
  date_of_birth: "Date of Birth",
  login: "Login Pattern",
  nickname: "Nickname"
}

/** Shortened labels for compact display (e.g. in tables) */
export const CRITERIA_LABELS_SHORT: Record<string, string> = {
  payment_details: "Payment",
  device_info: "Device",
  browser_info: "Browser",
  ipv6: "IPv6",
  email: "Email",
  full_name: "Name",
  address: "Address",
  date_of_birth: "DOB",
  login: "Login",
  nickname: "Nickname"
}

// ─── Factor configuration ───────────────────────────────────────────────────

export const RISK_CATEGORIES: RiskCategory[] = ["financial", "profile", "behavioral"]

export const FACTOR_CONFIG: Record<RiskCategory, { key: string; label: string }[]> = {
  financial: [
    { key: "money_laundering", label: "Money Laundering" },
    { key: "high_volatility", label: "High Volatility" },
    { key: "negative_profit", label: "Negative Profit" },
    { key: "high_roller", label: "High Roller" }
  ],
  profile: [
    { key: "duplicates", label: "Duplicates" },
    { key: "abandoned_profile", label: "Abandoned Profile" },
    { key: "empty_profile", label: "Empty Profile" },
    { key: "blacklist", label: "Blacklist" }
  ],
  behavioral: [
    { key: "bonus_hunting", label: "Bonus Hunting" },
    { key: "stolen_account", label: "Stolen Account" },
    { key: "vpn_usage", label: "VPN Usage" },
    { key: "martingale", label: "Martingale" }
  ]
}

// ─── Blacklist types ────────────────────────────────────────────────────────

export const BLACKLIST_TYPES: { value: string; label: string }[] = [
  { value: "EMAIL", label: "Email" },
  { value: "NAME", label: "Name" },
  { value: "IP", label: "IP Address" },
  { value: "PAYMENT", label: "Payment Method" },
  { value: "DEVICE", label: "Device" }
]

export const BLACKLIST_TYPE_BADGE: Record<string, string> = {
  EMAIL: "badge-error",
  NAME: "badge-warning",
  IP: "badge-info",
  PAYMENT: "badge-accent",
  DEVICE: "badge-ghost"
}

export const BLACKLIST_PLACEHOLDER: Record<string, string> = {
  EMAIL: "user@example.com",
  NAME: "John Doe",
  IP: "192.168.1.1",
  PAYMENT: "Card ending 4242",
  DEVICE: "Device fingerprint"
}

// ─── Condition factors (auto-action rules) ──────────────────────────────────

export const CONDITION_FACTORS = [
  { value: "GENERAL_RISK", label: "General Risk Level" },
  { value: "money_laundering", label: "Money Laundering" },
  { value: "high_volatility", label: "High Volatility" },
  { value: "negative_profit", label: "Negative Profit" },
  { value: "high_roller", label: "High Roller" },
  { value: "duplicates", label: "Duplicates" },
  { value: "abandoned_profile", label: "Abandoned Profile" },
  { value: "empty_profile", label: "Empty Profile" },
  { value: "blacklist", label: "Blacklist" },
  { value: "bonus_hunting", label: "Bonus Hunting" },
  { value: "stolen_account", label: "Stolen Account" },
  { value: "vpn_usage", label: "VPN Usage" },
  { value: "martingale", label: "Martingale" }
]

export const OPERATORS = [">=", ">", "=", "<=", "<"] as const

export const SEVERITIES = ["NO_RISK", "MEDIUM", "HIGH", "TOO_HIGH"] as const
export type RiskSeverity = (typeof SEVERITIES)[number]
export const LEVELS = ["LEVEL_1", "LEVEL_2", "LEVEL_3", "LEVEL_4"] as const

export const ACTION_TYPES = [
  { value: "FLAG_FOR_REVIEW", label: "Flag for Review" },
  //   { value: "LIMIT_DEPOSITS", label: "Limit Deposits" },
  { value: "FORBID_BONUSES", label: "Forbid Bonuses" },
  { value: "DISABLE_WITHDRAWALS", label: "Disable Withdrawals" },
  //   { value: "REQUIRE_KYC", label: "Require KYC" },
  { value: "SEND_ALERT", label: "Send Alert" },
  { value: "BAN_PLAYER", label: "Permanent Exclusion" }
]

// ─── Manual actions (player risk tab) ───────────────────────────────────────

export const MANUAL_ACTIONS = [
  { key: "FLAG_FOR_REVIEW", label: "Flag for Review" },
  { key: "FORBID_BONUSES", label: "Forbid Bonuses" },
  { key: "DISABLE_WITHDRAWALS", label: "Disable Withdrawals" },
  { key: "BAN_PLAYER", label: "Permanent Exclusion", danger: true }
]

// ─── Tab configurations ─────────────────────────────────────────────────────

export const TOP_TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "review", label: "Review Queue" },
  { key: "settings", label: "Settings" }
]

export const SETTINGS_TABS = [
  { key: "levels", label: "Risk Levels" },
  { key: "duplicates", label: "Duplicate Detection" },
  { key: "actions", label: "Auto Actions" },
  { key: "blacklist", label: "Blacklist" }
]

export const REVIEW_TABS = [
  { key: "flagged", label: "Flagged Players" },
  { key: "duplicates", label: "Duplicate Matches" }
]

// ─── Dropdown options (for DropdownMenu component) ─────────────────────────

export const BLACKLIST_TYPE_OPTIONS = BLACKLIST_TYPES.map((t) => ({ name: t.label, value: t.value }))

export const REVIEW_STATUS_OPTIONS = [
  { name: "Pending", value: "PENDING" },
  { name: "Reviewed", value: "REVIEWED" },
  { name: "Dismissed", value: "DISMISSED" }
]

export const REVIEW_PRIORITY_OPTIONS = [
  { name: "High", value: "HIGH" },
  { name: "Medium", value: "MEDIUM" },
  { name: "Low", value: "LOW" }
]

export const BLACKLIST_ACTIVE_OPTIONS = [
  { name: "Active", value: "true" },
  { name: "Inactive", value: "false" }
]

export const DUPLICATE_STATUS_OPTIONS = [
  { name: "Pending", value: "PENDING" },
  { name: "Confirmed", value: "CONFIRMED" },
  { name: "Dismissed", value: "DISMISSED" }
]

export const DUPLICATE_SEVERITY_OPTIONS = [
  { name: "Medium", value: "MEDIUM" },
  { name: "High", value: "HIGH" },
  { name: "Too High", value: "TOO_HIGH" }
]

// ─── Chart tooltip style ────────────────────────────────────────────────────

export const CHART_TOOLTIP_STYLE = {
  background: "#1f2937",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 8,
  fontSize: 12
}

// ─── History chart line config ──────────────────────────────────────────────

export const HISTORY_LINE_CONFIG = [
  { key: "total" as const, label: "Total", color: "#a855f7" },
  { key: "financial" as const, label: "Financial", color: "#f43f5e" },
  { key: "profile" as const, label: "Profile", color: "#f59e0b" },
  { key: "behavioral" as const, label: "Behavioral", color: "#06b6d4" }
]
